import { useState } from 'react';
import { Outlet, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { adminApi, financialEntriesApi } from '../../api/client';
import {
    Layout,
    Menu,
    Avatar,
    Dropdown,
    Button,
    Space,
    theme,
    Typography,
    FloatButton,
    Drawer,
    Grid,
} from 'antd';
import {
    DashboardOutlined,
    ShoppingOutlined,
    InboxOutlined,
    FileTextOutlined,
    ProjectOutlined,
    SettingOutlined,
    UserOutlined,
    LogoutOutlined,
    MenuFoldOutlined,
    MenuUnfoldOutlined,
    DollarOutlined,
    AimOutlined,
    ApiOutlined,
    PhoneOutlined,
    BulbOutlined,
    MoonOutlined,
    MessageOutlined,
    MenuOutlined,
    LinkOutlined,
    BarChartOutlined,
} from '@ant-design/icons';
import { useAuthStore } from '../../store/auth.store';
import { useThemeStore } from '../../store/theme.store';
import NotificationBell from '../notifications/NotificationBell';
import { ProfessionalModeIcon } from './ProfessionalModeIcon';
import FloatingFeedback from './FloatingFeedback';

const { Header, Sider, Content } = Layout;
const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const menuItems = [
    // ... same as before
    {
        key: '/dashboard',
        resource: 'orders',
        icon: <DashboardOutlined />,
        label: 'Dashboard',
        featureCode: 'MODULE_DASHBOARD',
    },
    {
        key: '/orders',
        resource: 'orders',
        icon: <FileTextOutlined />,
        label: 'Zamówienia',
        featureCode: 'MODULE_ORDERS',
    },
    {
        key: '/products',
        resource: 'products',
        icon: <ShoppingOutlined />,
        label: 'Produkty',
        featureCode: 'MODULE_PRODUCTS',
    },
    {
        key: '/inventory',
        resource: 'inventory',
        icon: <InboxOutlined />,
        label: 'Magazyn',
        featureCode: 'MODULE_INVENTORY',
    },
    {
        key: '/b2b',
        resource: 'partners',
        icon: <UserOutlined />,
        label: 'Kontrahenci B2B',
        featureCode: 'MODULE_B2B',
    },
    {
        key: '/crm',
        resource: 'customers',
        icon: <PhoneOutlined />,
        label: 'Opinie',
        featureCode: 'MODULE_CRM',
    },
    {
        key: '/tasks',
        resource: 'tasks',
        icon: <ProjectOutlined />,
        label: 'Zadania',
        featureCode: 'MODULE_TASKS',
    },
    {
        key: 'trello',
        resource: 'tasks',
        icon: <LinkOutlined />,
        label: 'Zadania Trello',
        url: 'https://trello.com/b/m78iud5i/zadania',
    },
    {
        key: '/finance',
        resource: 'costs',
        icon: <DollarOutlined />,
        label: 'Koszty',
        featureCode: 'MODULE_FINANCE',
    },
    {
        key: '/financial-hub',
        resource: 'budgets',
        icon: <DollarOutlined />,
        label: 'Finanse',
        featureCode: 'MODULE_FINANCIAL_HUB',
    },
    {
        key: '/strategy',
        resource: 'strategy',
        icon: <AimOutlined />,
        label: 'Strategia',
        featureCode: 'MODULE_STRATEGY',
    },
    {
        key: '/sales-planning',
        resource: 'forecasts',
        icon: <DollarOutlined />,
        label: 'Planowanie Sprzedaży',
        featureCode: 'MODULE_SALES_PLANNING',
    },
    {
        key: '/bdo',
        resource: 'bdo',
        icon: <BarChartOutlined />,
        label: 'BDO',
    },
    {
        key: '/integrations',
        resource: 'integrations',
        icon: <ApiOutlined />,
        label: 'Integracje',
    },
    {
        key: '/admin',
        icon: <SettingOutlined />,
        label: 'Administracja',
    },
];

export default function MainLayout() {
    const screens = useBreakpoint();
    const isMobile = !screens.md;
    const [collapsed, setCollapsed] = useState(false);
    const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [feedbackOpen, setFeedbackOpen] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const { user, logout, hasPermission } = useAuthStore();
    const { token } = theme.useToken();
    const { isDarkMode, toggleTheme, isCustomCursorActive } = useThemeStore();

    const handleMenuClick = ({ key }: { key: string }) => {
        const item = (visibleMenuItems as any[]).find(i => i.key === key);
        if (item?.url) {
            window.open(item.url, '_blank');
            if (isMobile) setMobileMenuOpen(false);
            return;
        }
        navigate(key);
        if (isMobile) setMobileMenuOpen(false);
    };

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const userMenuItems = [
        {
            key: 'profile',
            icon: <UserOutlined />,
            label: 'Profil',
        },
        {
            type: 'divider' as const,
        },
        {
            key: 'logout',
            icon: <LogoutOutlined />,
            label: 'Wyloguj',
            onClick: handleLogout,
        },
    ];

    // Zakładka jest widoczna, jeśli użytkownik ma jakiekolwiek uprawnienie do jej zasobu (np. customers:read)
    const hasResource = (resource: string) =>
        !!user?.permissions?.some((p) => p === '*:*' || p.startsWith(`${resource}:`));

    const { data: featureFlags } = useQuery({
        queryKey: ['featureFlags'],
        queryFn: async () => {
            if (!hasPermission('settings:read') && !hasPermission('*:*')) return []; // Basic security check
            return (await adminApi.getFeatureFlags()).data;
        },
        staleTime: 60000,
    });

    const { data: finHubSummary } = useQuery({
        queryKey: ['financial-hub-menu-status'],
        enabled: hasResource('budgets'),
        queryFn: async () => {
            try {
                const res = await financialEntriesApi.getSummary();
                return res.data;
            } catch (e) {
                return {
                    overdueCount: 0,
                    pendingCount: 0,
                    menuStatus: 'green',
                };
            }
        },
        staleTime: 30000,
    });

    const visibleMenuItems = menuItems.filter((item: any) => {
        if (item.featureCode && featureFlags) {
            const flag = featureFlags.find((f: any) => f.code === item.featureCode);
            if (flag && !flag.isEnabled) {
                return false;
            }
        }

        if (item.key === '/admin') {
            return ['users', 'roles', 'dictionaries', 'settings', 'feedback'].some(hasResource);
        }
        if (item.key === '/users') {
            return hasPermission('users:read') || hasPermission('*:*');
        }
        if (item.resource) {
            return hasResource(item.resource);
        }
        return true;
    });

    // Blokada wejścia z palca na zakładkę, do której użytkownik nie ma dostępu
    const currentMenuItem = menuItems.find(
        (item: any) => !item.url && (location.pathname === item.key || location.pathname.startsWith(`${item.key}/`)),
    );
    const firstAllowedPath = (visibleMenuItems as any[]).find((item) => !item.url)?.key;
    if (currentMenuItem && !visibleMenuItems.includes(currentMenuItem) && firstAllowedPath) {
        return <Navigate to={firstAllowedPath} replace />;
    }

    const mappedMenuItems = visibleMenuItems.map((item: any) => {
        if (item.key === '/financial-hub' && finHubSummary) {
            let color = '#52c41a'; // green
            let count = 0;
            if (finHubSummary.menuStatus === 'red') {
                color = '#ff4d4f'; // red
                count = finHubSummary.overdueCount;
            } else if (finHubSummary.menuStatus === 'orange') {
                color = '#faad14'; // orange
                count = finHubSummary.pendingCount;
            }

            return {
                ...item,
                label: (
                    <Space style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                        <span>{item.label}</span>
                        {count > 0 ? (
                            <span
                                style={{
                                    display: 'inline-block',
                                    padding: '0 6px',
                                    fontSize: '11px',
                                    lineHeight: '16px',
                                    borderRadius: '8px',
                                    backgroundColor: color,
                                    color: '#fff',
                                    fontWeight: 'bold',
                                    minWidth: '20px',
                                    textAlign: 'center',
                                }}
                            >
                                {count}
                            </span>
                        ) : (
                            <span
                                style={{
                                    display: 'inline-block',
                                    width: '8px',
                                    height: '8px',
                                    borderRadius: '50%',
                                    backgroundColor: '#52c41a',
                                    marginRight: '6px',
                                }}
                            />
                        )}
                    </Space>
                ),
            };
        }
        return item;
    });

    return (
        <Layout style={{ minHeight: '100vh', cursor: isCustomCursorActive ? 'url("/kursor1.ico"), auto' : 'auto' }}>
            <style>
                {isCustomCursorActive && `
                    *, * *, .ant-btn, .ant-menu-item, .ant-layout-sider-trigger {
                        cursor: url("/kursor1.ico"), auto !important;
                    }
                `}
            </style>
            {!isMobile && (
                <Sider
                    trigger={null}
                    collapsible
                    collapsed={collapsed}
                    style={{
                        background: token.colorBgContainer,
                        borderRight: `1px solid ${token.colorBorderSecondary}`,
                    }}
                >
                    <div
                        style={{
                            height: 64,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderBottom: `1px solid ${token.colorBorderSecondary}`,
                            flexDirection: 'column',
                        }}
                    >
                        <h2 style={{ margin: 0, color: token.colorPrimary }}>
                            {collapsed ? 'CC' : 'CureCraft'}
                        </h2>
                    </div>
                    <Menu
                        mode="inline"
                        selectedKeys={[location.pathname]}
                        items={mappedMenuItems}
                        onClick={handleMenuClick}
                        style={{ borderRight: 0 }}
                    />
                </Sider>
            )}

            {isMobile && (
                <Drawer
                    title="CureCraft Menu"
                    placement="left"
                    onClose={() => setMobileMenuOpen(false)}
                    open={isMobileMenuOpen}
                    bodyStyle={{ padding: 0 }}
                >
                    <Menu
                        mode="inline"
                        selectedKeys={[location.pathname]}
                        items={mappedMenuItems}
                        onClick={handleMenuClick}
                        style={{ borderRight: 0 }}
                    />
                </Drawer>
            )}

            <Layout>
                <Header
                    style={{
                        padding: '0 24px',
                        background: token.colorBgContainer,
                        borderBottom: `1px solid ${token.colorBorderSecondary}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    {isMobile ? (
                        <Button
                            type="text"
                            icon={<MenuOutlined />}
                            onClick={() => setMobileMenuOpen(true)}
                        />
                    ) : (
                        <Button
                            type="text"
                            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                            onClick={() => setCollapsed(!collapsed)}
                        />
                    )}
                    <Space size="middle">
                        <Button
                            type="text"
                            icon={isDarkMode ? <BulbOutlined /> : <MoonOutlined />}
                            onClick={toggleTheme}
                            title={isDarkMode ? 'Przełącz na jasny motyw' : 'Przełącz na ciemny motyw'}
                            style={{ fontSize: '16px' }}
                        />
                        <ProfessionalModeIcon />
                        <NotificationBell />
                        <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
                            <Space style={{ cursor: 'pointer' }}>
                                <Avatar icon={<UserOutlined />} />
                                {!isMobile && (
                                    <span>
                                        {user?.firstName} {user?.lastName}
                                    </span>
                                )}
                            </Space>
                        </Dropdown>
                    </Space>
                </Header>
                <Content
                    style={{
                        margin: 24,
                        minHeight: 280,
                    }}
                >
                    <Outlet />
                </Content>
                <FloatButton
                    icon={<MessageOutlined />}
                    tooltip="Zgłoś błąd lub pomysł"
                    onClick={() => setFeedbackOpen(true)}
                    style={{ position: 'absolute', right: 24, bottom: 24 }}
                />
                <FloatingFeedback isOpen={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
            </Layout>
        </Layout>
    );
}
